/**
 * 통합 레이아웃 관리 데모 페이지 (Mock 버전)
 * API 호출 없이 Mock 데이터만 표시
 * Drag & Drop, localStorage 저장/불러오기 기능 포함
 */

import React, { useState, useEffect } from 'react';
import {
  RectangleGroupIcon,
  PlusIcon,
  Cog6ToothIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import DemoNavigation from '../components/DemoNavigation';

// Mock 데이터
const mockRooms = [
  { id: '1', name: '1층 독서실', grid_width: 12, grid_height: 8 },
  { id: '2', name: '2층 독서실', grid_width: 10, grid_height: 6 },
];

const mockZones = [
  { id: '1', name: '프리미엄존', color: '#8B5CF6', room_id: '1' },
  { id: '2', name: '일반존', color: '#3B82F6', room_id: '1' },
  { id: '3', name: '창가존', color: '#10B981', room_id: '1' },
];

const initialMockSeats = [
  // 프리미엄존 (보라색)
  { id: '1', room_id: '1', zone_id: '1', seat_number: 'A1', position_x: 1, position_y: 1, is_occupied: false },
  { id: '2', room_id: '1', zone_id: '1', seat_number: 'A2', position_x: 2, position_y: 1, is_occupied: true },
  { id: '3', room_id: '1', zone_id: '1', seat_number: 'A3', position_x: 3, position_y: 1, is_occupied: false },
  { id: '4', room_id: '1', zone_id: '1', seat_number: 'A4', position_x: 4, position_y: 1, is_occupied: false },

  // 일반존 (파란색)
  { id: '5', room_id: '1', zone_id: '2', seat_number: 'B1', position_x: 6, position_y: 1, is_occupied: true },
  { id: '6', room_id: '1', zone_id: '2', seat_number: 'B2', position_x: 7, position_y: 1, is_occupied: false },
  { id: '7', room_id: '1', zone_id: '2', seat_number: 'B3', position_x: 8, position_y: 1, is_occupied: false },
  { id: '8', room_id: '1', zone_id: '2', seat_number: 'B4', position_x: 9, position_y: 1, is_occupied: true },

  // 창가존 (초록색)
  { id: '9', room_id: '1', zone_id: '3', seat_number: 'C1', position_x: 1, position_y: 4, is_occupied: false },
  { id: '10', room_id: '1', zone_id: '3', seat_number: 'C2', position_x: 2, position_y: 4, is_occupied: false },
  { id: '11', room_id: '1', zone_id: '3', seat_number: 'C3', position_x: 3, position_y: 4, is_occupied: true },
  { id: '12', room_id: '1', zone_id: '3', seat_number: 'C4', position_x: 4, position_y: 4, is_occupied: false },

  // 일반존 추가 좌석
  { id: '13', room_id: '1', zone_id: '2', seat_number: 'B5', position_x: 6, position_y: 4, is_occupied: false },
  { id: '14', room_id: '1', zone_id: '2', seat_number: 'B6', position_x: 7, position_y: 4, is_occupied: false },
  { id: '15', room_id: '1', zone_id: '2', seat_number: 'B7', position_x: 8, position_y: 4, is_occupied: true },
  { id: '16', room_id: '1', zone_id: '2', seat_number: 'B8', position_x: 9, position_y: 4, is_occupied: false },
];

// localStorage 키
const STORAGE_KEY = 'demoLayoutSeats';

// Drag & Drop 아이템 타입
const ItemTypes = {
  SEAT: 'seat',
};

// 좌석 타입 정의
interface Seat {
  id: string;
  room_id: string;
  zone_id: string;
  seat_number: string;
  position_x: number;
  position_y: number;
  is_occupied: boolean;
}

// Draggable Seat Component
interface DraggableSeatProps {
  seat: Seat;
  zoneColor: string;
  onSelect: (seatId: string) => void;
  isSelected: boolean;
}

const DraggableSeat: React.FC<DraggableSeatProps> = ({ seat, zoneColor, onSelect, isSelected }) => {
  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.SEAT,
    item: { id: seat.id, seat },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  return (
    <div
      ref={drag}
      onClick={() => onSelect(seat.id)}
      className={`aspect-square rounded-lg border-2 flex items-center justify-center text-sm font-semibold transition-all cursor-move ${
        seat.is_occupied
          ? 'bg-gray-300 border-gray-400 text-gray-600'
          : 'bg-white border-current hover:shadow-lg'
      } ${isSelected ? 'ring-4 ring-yellow-400' : ''} ${isDragging ? 'opacity-50' : ''}`}
      style={{
        borderColor: seat.is_occupied ? undefined : zoneColor,
        color: seat.is_occupied ? undefined : zoneColor,
      }}
      title={seat.is_occupied ? '사용 중' : '사용 가능'}
    >
      {seat.seat_number}
    </div>
  );
};

// Droppable Grid Cell Component
interface GridCellProps {
  x: number;
  y: number;
  seat: Seat | undefined;
  onDrop: (seatId: string, x: number, y: number) => void;
  zoneColor: string;
  onSelect: (seatId: string) => void;
  isSelected: boolean;
}

const GridCell: React.FC<GridCellProps> = ({ x, y, seat, onDrop, zoneColor, onSelect, isSelected }) => {
  const [{ isOver }, drop] = useDrop({
    accept: ItemTypes.SEAT,
    drop: (item: { id: string; seat: Seat }) => {
      onDrop(item.id, x + 1, y + 1);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  if (seat) {
    return (
      <div ref={drop} className={isOver ? 'bg-blue-100' : ''}>
        <DraggableSeat seat={seat} zoneColor={zoneColor} onSelect={onSelect} isSelected={isSelected} />
      </div>
    );
  }

  return (
    <div
      ref={drop}
      className={`aspect-square ${isOver ? 'bg-blue-100 border-2 border-blue-400 border-dashed rounded-lg' : ''}`}
    />
  );
};

const DemoIntegratedLayoutPage: React.FC = () => {
  const [selectedRoom, setSelectedRoom] = useState(mockRooms[0]);
  const [selectedZone, setSelectedZone] = useState<string | null>(null);
  const [seats, setSeats] = useState<Seat[]>([]);
  const [selectedSeatId, setSelectedSeatId] = useState<string | null>(null);
  const [nextSeatNumber, setNextSeatNumber] = useState(17);

  // localStorage에서 좌석 불러오기
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        setSeats(JSON.parse(saved));
      } catch {
        setSeats(initialMockSeats);
      }
    } else {
      setSeats(initialMockSeats);
    }
  }, []);

  // 좌석이 변경될 때마다 localStorage에 저장
  useEffect(() => {
    if (seats.length > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(seats));
    }
  }, [seats]);

  // 좌석 드래그 & 드롭 핸들러
  const handleDrop = (seatId: string, newX: number, newY: number) => {
    setSeats((prevSeats) =>
      prevSeats.map((seat) =>
        seat.id === seatId
          ? { ...seat, position_x: newX, position_y: newY }
          : seat
      )
    );
  };

  // 좌석 추가
  const handleAddSeat = () => {
    const newSeat: Seat = {
      id: `new-${Date.now()}`,
      room_id: selectedRoom.id,
      zone_id: mockZones[0].id, // 기본값: 첫 번째 존
      seat_number: `N${nextSeatNumber}`,
      position_x: 1,
      position_y: 1,
      is_occupied: false,
    };
    setSeats([...seats, newSeat]);
    setNextSeatNumber(nextSeatNumber + 1);
  };

  // 좌석 삭제
  const handleDeleteSeat = () => {
    if (selectedSeatId) {
      setSeats(seats.filter(s => s.id !== selectedSeatId));
      setSelectedSeatId(null);
    }
  };

  // 레이아웃 초기화
  const handleReset = () => {
    if (window.confirm('레이아웃을 초기화하시겠습니까?')) {
      setSeats(initialMockSeats);
      setSelectedSeatId(null);
      localStorage.removeItem(STORAGE_KEY);
    }
  };

  const roomSeats = seats.filter(s => s.room_id === selectedRoom.id);
  const roomZones = mockZones.filter(z => z.room_id === selectedRoom.id);

  const filteredSeats = selectedZone
    ? roomSeats.filter(s => s.zone_id === selectedZone)
    : roomSeats;

  const getZoneColor = (zoneId: string) => {
    const zone = mockZones.find(z => z.id === zoneId);
    return zone?.color || '#3B82F6';
  };

  const occupiedCount = filteredSeats.filter(s => s.is_occupied).length;
  const totalCount = filteredSeats.length;

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="min-h-screen bg-gray-50">
      {/* 데모 헤더 */}
      <div className="bg-blue-600 text-white py-3 px-4 text-center">
        <p className="text-sm">
          📢 이것은 데모 페이지입니다. 모든 데이터는 가상 데이터이며, 데모 시연을 위해 주기적으로 업데이트되어 조금씩 변동될 수 있습니다.
        </p>
      </div>

      {/* 네비게이션 */}
      <DemoNavigation currentTitle="통합 레이아웃 관리 (데모)" currentIcon={RectangleGroupIcon} />

      {/* 메인 컨텐츠 */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* 왼쪽: 컨트롤 패널 */}
          <div className="lg:col-span-1 space-y-4">
            {/* 룸 선택 */}
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="text-lg font-semibold mb-4">룸 선택</h3>
              <div className="space-y-2">
                {mockRooms.map((room) => (
                  <button
                    key={room.id}
                    onClick={() => setSelectedRoom(room)}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${
                      selectedRoom.id === room.id
                        ? 'bg-blue-100 text-blue-700 font-medium'
                        : 'bg-gray-50 hover:bg-gray-100'
                    }`}
                  >
                    {room.name}
                  </button>
                ))}
              </div>
            </div>

            {/* 존 필터 */}
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="text-lg font-semibold mb-4">존 필터</h3>
              <div className="space-y-2">
                <button
                  onClick={() => setSelectedZone(null)}
                  className={`w-full text-left px-4 py-2 rounded-lg transition-colors ${
                    !selectedZone
                      ? 'bg-blue-100 text-blue-700 font-medium'
                      : 'bg-gray-50 hover:bg-gray-100'
                  }`}
                >
                  전체 보기
                </button>
                {roomZones.map((zone) => (
                  <button
                    key={zone.id}
                    onClick={() => setSelectedZone(zone.id)}
                    className={`w-full text-left px-4 py-2 rounded-lg transition-colors flex items-center ${
                      selectedZone === zone.id
                        ? 'bg-blue-100 text-blue-700 font-medium'
                        : 'bg-gray-50 hover:bg-gray-100'
                    }`}
                  >
                    <div
                      className="w-4 h-4 rounded mr-2"
                      style={{ backgroundColor: zone.color }}
                    />
                    {zone.name}
                  </button>
                ))}
              </div>
            </div>

            {/* 통계 */}
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="text-lg font-semibold mb-4">현황</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">총 좌석</span>
                  <span className="font-semibold">{totalCount}석</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">사용 중</span>
                  <span className="font-semibold text-red-600">{occupiedCount}석</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">사용 가능</span>
                  <span className="font-semibold text-green-600">{totalCount - occupiedCount}석</span>
                </div>
                <div className="pt-2 border-t">
                  <div className="flex justify-between mb-1">
                    <span className="text-sm text-gray-600">사용률</span>
                    <span className="text-sm font-semibold">
                      {((occupiedCount / totalCount) * 100).toFixed(1)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all"
                      style={{ width: `${(occupiedCount / totalCount) * 100}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* 좌석 관리 버튼들 */}
            <div className="bg-white rounded-lg shadow p-4 space-y-2">
              <button
                onClick={handleAddSeat}
                className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                새 좌석 추가
              </button>
              <button
                onClick={handleDeleteSeat}
                disabled={!selectedSeatId}
                className={`w-full flex items-center justify-center px-4 py-2 rounded-lg transition-colors ${
                  selectedSeatId
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                <TrashIcon className="h-5 w-5 mr-2" />
                선택한 좌석 삭제
              </button>
              <button
                onClick={handleReset}
                className="w-full flex items-center justify-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                <Cog6ToothIcon className="h-5 w-5 mr-2" />
                레이아웃 초기화
              </button>
            </div>
          </div>

          {/* 오른쪽: 좌석 배치도 */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="mb-6">
                <h2 className="text-2xl font-bold mb-2">{selectedRoom.name}</h2>
                <p className="text-gray-600">
                  {selectedZone
                    ? `${roomZones.find(z => z.id === selectedZone)?.name} 좌석`
                    : '전체 좌석 배치도'}
                </p>
              </div>

              {/* 좌석 그리드 */}
              <div className="relative bg-gray-50 rounded-lg p-8" style={{ minHeight: '500px' }}>
                <div className="grid gap-4" style={{
                  gridTemplateColumns: `repeat(${selectedRoom.grid_width}, 1fr)`,
                }}>
                  {Array.from({ length: selectedRoom.grid_height }).map((_, y) =>
                    Array.from({ length: selectedRoom.grid_width }).map((_, x) => {
                      const seat = filteredSeats.find(
                        s => s.position_x === x + 1 && s.position_y === y + 1
                      );
                      const zoneColor = seat ? getZoneColor(seat.zone_id) : '#3B82F6';

                      return (
                        <GridCell
                          key={`${x}-${y}`}
                          x={x}
                          y={y}
                          seat={seat}
                          onDrop={handleDrop}
                          zoneColor={zoneColor}
                          onSelect={setSelectedSeatId}
                          isSelected={seat?.id === selectedSeatId}
                        />
                      );
                    })
                  )}
                </div>
              </div>

              {/* 범례 */}
              <div className="mt-6 flex items-center justify-center gap-6">
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-white border-2 border-blue-500 rounded mr-2" />
                  <span className="text-sm text-gray-600">사용 가능</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-gray-300 border-2 border-gray-400 rounded mr-2" />
                  <span className="text-sm text-gray-600">사용 중</span>
                </div>
              </div>

              <div className="mt-4 text-center text-gray-500 text-sm">
                💡 좌석을 드래그하여 위치를 변경할 수 있습니다. 변경사항은 자동으로 저장됩니다.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </DndProvider>
  );
};

export default DemoIntegratedLayoutPage;
